import { useState } from 'react';
import { Container, Card, CardContent, Grid, Stack, TextField, MenuItem, Typography, Avatar, Box, Autocomplete, Link } from '@mui/material';
import { ImageOutlined as ImageOutlinedIcon } from '@mui/icons-material';
import { checkUsernameAvailable, uploadImage } from '../../lib/api';
import { COUNTRIES } from '../../lib/countries';
import { CREATOR_TYPE_OPTIONS, NICHE_OPTIONS } from '../../lib/creatorOptions';
import { countryFlag } from '../../lib/social';
import Button from '../../components/Button';
import EditableTagEditor from '../../components/EditableTagEditor';
import LanguagePicker from '../../components/LanguagePicker';
import PageHeader from '../../components/PageHeader';
import ProfilePreview from '../../components/ProfilePreview';
import ConfirmDialog from '../../components/ConfirmDialog';
import ProfileCompletionPanel from '../../components/ProfileCompletionPanel';
import PublishProfileDialog from '../../components/PublishProfileDialog';
import { normalizeUsername, usernameError } from '../../lib/profileReadiness';
import { useProfileCompletion } from '../../lib/useProfileCompletion';
import { navigate } from '../../lib/navigation';
import OpportunityProfileEditor from '../../components/OpportunityProfileEditor';

function fieldsDiffer(form, saved, fields) {
  return fields.some((field) => JSON.stringify(form[field]) !== JSON.stringify(saved[field]));
}

function PublishStatusIndicator({ published }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: published ? 'success.main' : 'action.disabled',
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: published ? 'success.main' : 'text.secondary' }}
      >
        {published ? 'Published' : 'Draft'}
      </Typography>
    </Stack>
  );
}

function Section({ id, title, titleExtra, incomplete, editing, onEdit, dirty, onSave, onDiscard, children }) {
  const editGated = typeof onEdit === 'function';
  return (
    <Card
      id={id}
      variant="outlined"
      sx={{
        scrollMarginTop: 88,
        borderColor: incomplete === true ? 'warning.main' : undefined,
      }}
    >
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Typography variant="h6">{title}</Typography>
            {titleExtra}
            {incomplete && (
              <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'warning.main' }} />
                <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                  Incomplete
                </Typography>
              </Stack>
            )}
            {incomplete === false && (
              <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }} aria-label={`${title}: Complete`}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  Complete
                </Typography>
              </Stack>
            )}
          </Stack>
          {editGated ? (
            editing ? (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button variant="muted" onClick={onDiscard} sx={{ whiteSpace: 'nowrap' }}>
                  Discard changes
                </Button>
                <Button onClick={onSave} sx={{ whiteSpace: 'nowrap' }}>
                  Save changes
                </Button>
              </Stack>
            ) : (
              <Button variant="secondary" onClick={onEdit} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Edit
              </Button>
            )
          ) : (
            dirty && (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button variant="muted" onClick={onDiscard} sx={{ whiteSpace: 'nowrap' }}>
                  Discard changes
                </Button>
                <Button onClick={onSave} sx={{ whiteSpace: 'nowrap' }}>
                  Save changes
                </Button>
              </Stack>
            )
          )}
        </Stack>
        <Grid container spacing={2}>
          {children}
        </Grid>
      </CardContent>
    </Card>
  );
}

function ProfilePage({ identity, identityActions, auth }) {
  const [form, setForm] = useState(identity.creator);
  const [usernameCheck, setUsernameCheck] = useState({ status: 'idle', message: '' });
  const [usernameEditing, setUsernameEditing] = useState(!identity.creator.username);
  const [uploadingField, setUploadingField] = useState(null);
  const [editingSections, setEditingSections] = useState({});
  const [publishOpen, setPublishOpen] = useState(false);
  const [pendingUnpublishPatch, setPendingUnpublishPatch] = useState(null);
  const { sections, loading: completionLoading } = useProfileCompletion(identity.creator);

  function startEditing(key) {
    setEditingSections((current) => ({ ...current, [key]: true }));
  }

  function stopEditing(key) {
    setEditingSections((current) => ({ ...current, [key]: false }));
  }

  function saveFields(fields) {
    identityActions.updateCreator(Object.fromEntries(fields.map((field) => [field, form[field]])));
  }

  function saveSection(fields, key) {
    saveFields(fields);
    stopEditing(key);
  }

  function discardSection(fields, key) {
    discardFields(fields);
    stopEditing(key);
  }

  function saveUsername() {
    const normalized = normalizeUsername(form.username);
    const usernameChanged = normalized !== identity.creator.username;
    if (usernameChanged && usernameCheck.status !== 'available') {
      identityActions.showErrorToast('Please check username availability before saving');
      return false;
    }
    setForm((current) => ({ ...current, username: normalized }));
    identityActions.updateCreator({ username: normalized });
    return true;
  }

  function discardUsername() {
    discardFields(usernameFields);
    setUsernameCheck({ status: 'idle', message: '' });
    setUsernameEditing(!identity.creator.username);
  }

  function handleUsernamePrimaryAction() {
    if (!usernameEditing) {
      setUsernameEditing(true);
      return;
    }
    const usernameChanged = form.username.trim() !== identity.creator.username;
    if (usernameCheck.status === 'available' && usernameChanged) {
      if (saveUsername()) {
        setUsernameEditing(false);
        setUsernameCheck({ status: 'idle', message: '' });
      }
      return;
    }
    checkUsername();
  }

  async function handlePhotoUpload(file, field) {
    if (!file) return;
    setUploadingField(field);
    try {
      const { url } = await uploadImage(file);
      setForm((current) => ({ ...current, [field]: url }));
      identityActions.updateCreator({ [field]: url });
    } catch (error) {
      identityActions.showErrorToast(error.message || 'Image upload failed');
    } finally {
      setUploadingField(null);
    }
  }

  function discardFields(fields) {
    setForm((current) => ({ ...current, ...Object.fromEntries(fields.map((field) => [field, identity.creator[field]])) }));
  }

  async function checkUsername() {
    const validationMessage = usernameError(form.username);
    if (validationMessage) {
      setUsernameCheck({ status: 'error', message: validationMessage });
      return;
    }
    setUsernameCheck({ status: 'checking', message: '' });
    try {
      const available = await checkUsernameAvailable(normalizeUsername(form.username));
      setUsernameCheck(
        available ? { status: 'available', message: 'Username is available' } : { status: 'taken', message: 'Username is already taken' }
      );
    } catch {
      setUsernameCheck({ status: 'error', message: 'Could not check availability, try again' });
    }
  }

  function saveBasicInfo() {
    const patch = Object.fromEntries(basicInfoFields.map((field) => [field, form[field]]));
    if (identity.creator.published && (!form.displayName.trim() || !form.headline.trim())) {
      setPendingUnpublishPatch({
        patch,
        section: 'basicInfo',
        description:
          'Display name and professional headline are required while your profile is live. Saving these changes will unpublish it.',
      });
      return;
    }
    identityActions.updateCreator(patch);
    stopEditing('basicInfo');
  }

  function saveLocation() {
    const patch = Object.fromEntries(locationFields.map((field) => [field, form[field]]));
    if (identity.creator.published && !form.location.trim()) {
      setPendingUnpublishPatch({
        patch,
        section: 'location',
        description: 'Location is required while your profile is live. Saving this change will unpublish it.',
      });
      return;
    }
    identityActions.updateCreator(patch);
    stopEditing('location');
  }

  const usernameChanged = normalizeUsername(form.username) !== identity.creator.username;
  const usernameSaveReady = usernameCheck.status === 'available' && usernameChanged;
  const usernameActionLabel = !usernameEditing
    ? 'Edit username'
    : usernameCheck.status === 'checking'
      ? 'Checking…'
      : usernameSaveReady
        ? 'Save username'
        : 'Check availability';
  const usernameActionDisabled =
    usernameEditing &&
    !usernameSaveReady &&
    (!form.username.trim() || form.username.trim() === identity.creator.username || usernameCheck.status === 'checking');

  const usernameFields = ['username'];
  const basicInfoFields = ['displayName', 'headline', 'bio'];
  const photoFields = ['avatarUrl', 'coverUrl'];
  const locationFields = ['location', 'languages'];
  const availabilityFields = ['availability', 'contactInfo'];
  const identityFields = ['creatorTypes', 'niches'];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader title="Profile editor" description="This is what brands see when they view your CreatorCV." />
      <ProfileCompletionPanel sections={sections} loading={completionLoading} />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={3}>
            <Section
              id="public-profile-status"
              title="Publishing"
              titleExtra={<PublishStatusIndicator published={identity.creator.published} />}
            >
              <Grid size={12}>
                <Stack spacing={1}>
                  <TextField
                    fullWidth
                    label="Username"
                    disabled={!usernameEditing}
                    value={form.username}
                    onChange={(event) => {
                      setForm({ ...form, username: event.target.value });
                      setUsernameCheck({ status: 'idle', message: '' });
                    }}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="secondary"
                      disabled={usernameActionDisabled}
                      onClick={handleUsernamePrimaryAction}
                      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      {usernameActionLabel}
                    </Button>
                    {usernameEditing && identity.creator.username && (
                      <Button variant="muted" onClick={discardUsername} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                        Discard changes
                      </Button>
                    )}
                  </Stack>
                  {usernameCheck.message && (
                    <Typography variant="caption" sx={{ color: usernameCheck.status === 'available' ? 'success.main' : 'error.main' }}>
                      {usernameCheck.message}
                    </Typography>
                  )}
                  {!identity.creator.username && (
                    <Typography variant="caption" color="text.secondary">
                      Your public profile URL: creatorcv.app/profile/your-username
                    </Typography>
                  )}
                  {identity.creator.username && identity.creator.published && (
                    <Typography variant="caption" color="text.secondary">
                      Your public profile URL:{' '}
                      <Link href={`/profile/${identity.creator.username}`} target="_blank" rel="noreferrer">
                        {window.location.host}/profile/{identity.creator.username}
                      </Link>
                    </Typography>
                  )}
                  {identity.creator.username && !identity.creator.published && (
                    <Typography variant="caption" color="text.secondary">
                      Will be live at {window.location.host}/profile/{identity.creator.username} (publish your profile to go live)
                    </Typography>
                  )}
                  <Box>
                    {!identity.creator.published && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mb: 1, color: 'grey.600', fontSize: '0.75rem', fontStyle: 'italic' }}
                      >
                        Your profile is currently a draft and cannot be viewed publicly. Publish it when you're ready to go live.
                      </Typography>
                    )}
                    <Button
                      variant={identity.creator.published ? 'reverse' : 'primary'}
                      fullWidth
                      onClick={() =>
                        identity.creator.published ? identityActions.updateCreator({ published: false }) : setPublishOpen(true)
                      }
                    >
                      {identity.creator.published ? 'Unpublish profile' : 'Publish profile'}
                    </Button>
                    <br />
                    {!identity.creator.username && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Set a username to publish your profile.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Grid>
            </Section>

            <Section
              id="basic-info"
              title="Basic info"
              incomplete={!identity.creator.displayName?.trim() || !identity.creator.headline?.trim()}
              editing={!!editingSections.basicInfo}
              onEdit={() => startEditing('basicInfo')}
              onSave={saveBasicInfo}
              onDiscard={() => discardSection(basicInfoFields, 'basicInfo')}
            >
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Display name"
                  disabled={!editingSections.basicInfo}
                  value={form.displayName}
                  onChange={(event) => setForm({ ...form, displayName: event.target.value })}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Professional headline"
                  placeholder="Write your professional headline"
                  helperText={
                    <>
                      Describe what you create and who it's for in one sentence.
                      <Box
                        component="span"
                        sx={{ display: 'block', mt: 0.25, fontSize: '0.72rem', fontStyle: 'italic', color: 'text.disabled' }}
                      >
                        Example: Beauty creator specialising in skincare reviews and tutorials.
                      </Box>
                    </>
                  }
                  disabled={!editingSections.basicInfo}
                  value={form.headline}
                  onChange={(event) => setForm({ ...form, headline: event.target.value })}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Bio"
                  placeholder="Write a short bio"
                  helperText={
                    <>
                      Share your content style, audience, experience, or what makes you distinctive.
                      <Box
                        component="span"
                        sx={{ display: 'block', mt: 0.25, fontSize: '0.72rem', fontStyle: 'italic', color: 'text.disabled' }}
                      >
                        Example: I create practical skincare content for young professionals, with a focus on sensitive skin and affordable
                        products.
                      </Box>
                    </>
                  }
                  disabled={!editingSections.basicInfo}
                  value={form.bio}
                  onChange={(event) => setForm({ ...form, bio: event.target.value })}
                />
              </Grid>
            </Section>

            <Section
              id="photos"
              title="Photos"
              incomplete={!identity.creator.avatarUrl?.trim()}
              dirty={fieldsDiffer(form, identity.creator, photoFields)}
              onSave={() => saveFields(photoFields)}
              onDiscard={() => discardFields(photoFields)}
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  <Avatar src={form.avatarUrl} sx={{ width: 56, height: 56 }} />
                  <Button variant="secondary" component="label" disabled={uploadingField === 'avatarUrl'}>
                    {uploadingField === 'avatarUrl' ? 'Uploading…' : form.avatarUrl ? 'Change photo' : 'Add photo'}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(event) => handlePhotoUpload(event.target.files?.[0], 'avatarUrl')}
                    />
                  </Button>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  {form.coverUrl ? (
                    <Box component="img" src={form.coverUrl} alt="" sx={{ width: 96, height: 56, objectFit: 'cover', borderRadius: 1 }} />
                  ) : (
                    <Box
                      sx={{
                        width: 96,
                        height: 56,
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ImageOutlinedIcon sx={{ color: 'grey.500' }} />
                    </Box>
                  )}
                  <Button variant="secondary" component="label" disabled={uploadingField === 'coverUrl'}>
                    {uploadingField === 'coverUrl' ? 'Uploading…' : form.coverUrl ? 'Change cover' : 'Add cover'}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(event) => handlePhotoUpload(event.target.files?.[0], 'coverUrl')}
                    />
                  </Button>
                </Stack>
              </Grid>
            </Section>

            <Section
              id="location-languages"
              title="Location & languages"
              incomplete={!identity.creator.location?.trim()}
              editing={!!editingSections.location}
              onEdit={() => startEditing('location')}
              onSave={saveLocation}
              onDiscard={() => discardSection(locationFields, 'location')}
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  fullWidth
                  disabled={!editingSections.location}
                  options={COUNTRIES}
                  value={form.location || null}
                  onChange={(event, newValue) => setForm({ ...form, location: newValue || '' })}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ gap: 1 }}>
                      <span>{countryFlag(option)}</span> {option}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location"
                      slotProps={{
                        ...params.slotProps,
                        input: {
                          ...params.slotProps?.input,
                          startAdornment: form.location ? <Box sx={{ pl: 0.5 }}>{countryFlag(form.location)}</Box> : null,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LanguagePicker
                  values={form.languages}
                  onChange={(languages) => setForm({ ...form, languages })}
                  disabled={!editingSections.location}
                />
              </Grid>
            </Section>

            <Section
              id="availability-contact"
              title="Availability & contact"
              incomplete={!identity.creator.availability?.trim() || !identity.creator.contactInfo?.trim()}
              editing={!!editingSections.availability}
              onEdit={() => startEditing('availability')}
              onSave={() => saveSection(availabilityFields, 'availability')}
              onDiscard={() => discardSection(availabilityFields, 'availability')}
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth disabled label="Account email (private)" value={auth?.user?.email || ''} />
                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'grey.600', fontSize: '0.75rem', fontStyle: 'italic' }}>
                  Private unless you add it to your contact instructions below.{' '}
                  <Link
                    href="/contact"
                    color="inherit"
                    onClick={(event) => {
                      event.preventDefault();
                      navigate('/contact');
                    }}
                  >
                    Contact our team
                  </Link>{' '}
                  to change it.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Availability"
                  disabled={!editingSections.availability}
                  value={form.availability}
                  onChange={(event) => setForm({ ...form, availability: event.target.value })}
                >
                  <MenuItem value="Available for collaborations">Available for collaborations</MenuItem>
                  <MenuItem value="Limited availability">Limited availability</MenuItem>
                  <MenuItem value="Not taking new work">Not taking new work</MenuItem>
                </TextField>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={8}
                  label="How should brands reach you?"
                  placeholder="e.g. DM me on Instagram @handle, or email me at you@example.com"
                  helperText="Shown on your public profile so brands know how you'd like to be contacted. Leave blank to not share a contact method."
                  disabled={!editingSections.availability}
                  value={form.contactInfo}
                  onChange={(event) => setForm({ ...form, contactInfo: event.target.value })}
                />
              </Grid>
            </Section>

            <Section
              id="creator-identity"
              title="Creator identity"
              incomplete={!identity.creator.creatorTypes?.length || !identity.creator.niches?.length}
              editing={!!editingSections.identity}
              onEdit={() => startEditing('identity')}
              onSave={() => saveSection(identityFields, 'identity')}
              onDiscard={() => discardSection(identityFields, 'identity')}
            >
              <Grid size={12}>
                <EditableTagEditor
                  label="Creator types"
                  placeholder="Add custom type"
                  values={form.creatorTypes}
                  onChange={(creatorTypes) => setForm({ ...form, creatorTypes })}
                  options={CREATOR_TYPE_OPTIONS}
                  disabled={!editingSections.identity}
                />
              </Grid>
              <Grid size={12}>
                <EditableTagEditor
                  label="Niches"
                  placeholder="Add custom niche"
                  values={form.niches}
                  onChange={(niches) => setForm({ ...form, niches })}
                  options={NICHE_OPTIONS}
                  disabled={!editingSections.identity}
                />
              </Grid>
            </Section>

            <OpportunityProfileEditor creator={identity.creator} onSave={identityActions.updateCreator} />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Public preview
          </Typography>
          <ProfilePreview creator={form} />
        </Grid>
      </Grid>
      <PublishProfileDialog
        open={publishOpen}
        creator={identity.creator}
        onClose={() => setPublishOpen(false)}
        onPublish={() => {
          setPublishOpen(false);
          identityActions.updateCreator({ published: true });
        }}
      />
      <ConfirmDialog
        open={Boolean(pendingUnpublishPatch)}
        title="Unpublish this profile?"
        description={pendingUnpublishPatch?.description || ''}
        confirmLabel="Save and unpublish"
        onClose={() => setPendingUnpublishPatch(null)}
        onConfirm={() => {
          identityActions.updateCreator({ ...pendingUnpublishPatch.patch, published: false });
          stopEditing(pendingUnpublishPatch.section);
          setPendingUnpublishPatch(null);
        }}
      />
    </Container>
  );
}

export default ProfilePage;
